// Minimal typed fetch client for the intake-server public API.
//
// We intentionally don't use @sirius/http here even though it's source-only
// and framework-free: that package was designed for SPAs on a single origin
// (authHeader/onError/onSlow hooks, currentRoute() reads globalThis.location).
// For an embeddable UMD widget loaded by third-party sites via <script>, we
// want zero coupling to browser SPA conventions — just POST/GET with apiresp
// envelope unwrap. The intake-server responds via sirius-lib/apiresp which
// always returns {ok, status, data, message}.

import type { Locale } from './types'

const DEFAULT_TIMEOUT_MS = 8000

interface ApiRespEnvelope<T> {
  ok: boolean
  status: number
  data: T | null
  message?: string
}

export class IntakeApiError extends Error {
  readonly status: number
  readonly responseData: unknown

  constructor(message: string, status: number, responseData: unknown) {
    super(message)
    this.name = 'IntakeApiError'
    this.status = status
    this.responseData = responseData
  }
}

export interface Client {
  get<T>(path: string): Promise<T>
  post<T>(path: string, body: unknown): Promise<T>
}

interface ClientOpts {
  apiBase: string
  locale?: Locale
  fetchImpl?: typeof fetch
  timeoutMs?: number
}

export function createClient(opts: ClientOpts): Client {
	const base = opts.apiBase.replace(/\/+$/, '')
	// Codex major #2 fix: don't dereference globalThis.fetch before checking it
	// exists. Some environments (Node < 18, sandboxed) lack global fetch — we
	// want the SDK to surface a clear error, not a raw TypeError.
	let fetchImpl: typeof fetch | undefined = opts.fetchImpl
	if (!fetchImpl) {
		if (typeof globalThis.fetch === 'function') {
			fetchImpl = globalThis.fetch.bind(globalThis)
		}
	}
	if (!fetchImpl) {
		throw new Error(
			'@sirius/intake-callback: globalThis.fetch is not available in this environment; ' +
				'pass fetchImpl explicitly via init({ fetchImpl })',
		)
	}
	// const-копія, бо narrowing let-змінної не переживає замикання (send нижче)
	const doFetch: typeof fetch = fetchImpl
	const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS

  function headers(): Record<string, string> {
    const h: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    }
    const locale = opts.locale ?? detectLocale()
    if (locale) h['Accept-Language'] = locale
    return h
  }

  async function send<T>(method: 'GET' | 'POST', path: string, body: unknown): Promise<T> {
    const url = base + path
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)

    let response: Response
    try {
      response = await doFetch(url, {
        method,
        headers: headers(),
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      })
    } catch (err) {
      clearTimeout(timer)
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new IntakeApiError('request timeout', 0, null)
      }
      throw new IntakeApiError('network error', 0, null)
    }
    clearTimeout(timer)

    let parsed: ApiRespEnvelope<unknown> | null = null
    const text = await response.text()
    if (text) {
      try {
        parsed = JSON.parse(text) as ApiRespEnvelope<unknown>
      } catch {
        // Non-JSON body — surface as error with the raw text.
        throw new IntakeApiError(text || 'invalid response', response.status, text)
      }
    }

    if (!response.ok) {
      const msg = parsed?.message ?? `${method} ${path} → ${response.status}`
      throw new IntakeApiError(msg, response.status, parsed?.data ?? null)
    }

    // apiresp envelope: ok=true, data is the payload. If data is null/missing,
    // caller gets null cast as T (intentional — empty 2xx bodies exist).
    return (parsed?.data ?? null) as T
  }

  return {
    get: (path) => send('GET', path, undefined),
    post: (path, body) => send('POST', path, body),
  }
}

function detectLocale(): Locale | undefined {
  if (typeof navigator === 'undefined' || !navigator.language) return undefined
  const lang = navigator.language.toLowerCase()
  if (lang.startsWith('uk')) return 'uk'
  if (lang.startsWith('ru')) return 'ru'
  return 'en'
}
